import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Gift, ArrowRight } from 'lucide-react';
import { getTrialStatus, type TrialStatus } from '../../services/trialService';
import { useWallet } from '../../contexts/WalletContext';
import { TYPE } from '../../constants/typeScale';
import { trackTrialOfferShown, trackStarterPackOfferShown, trackStarterPackOfferClicked } from '../../services/analyticsService';

/**
 * THE FREE FIRST STORYBOARD, SAID PLAINLY (TRY-2 / TRY-4, owner 2026-09-27: no free credits; *"we should have a way
 * to encourage them to give it a try and invest into their own creativity"*).
 *
 * Three moments, one quiet line each (never an ad-like banner):
 *   - `home`: the trial is unused — *Your first storyboard is on us.*
 *   - the trial episode, before its keyframes: how many free panels are left.
 *   - the trial episode, once it has keyframes: *Bring it to life* — the next step is the starter pack (or the
 *     wallet), which is the moment a creator has seen their own story and wants it to move.
 * Nothing renders while the status loads or when it does not apply, so the Studio never flickers a promise.
 */
interface Props {
  where: 'home' | 'episode';
  /** The episode on screen (episode placement only). */
  storyboardId?: string | null;
  /** True once this episode has at least one preview keyframe. */
  hasKeyframes?: boolean;
  /** Bump to re-read the status (e.g. after a preview run). */
  refreshKey?: number;
}

const TrialNotice: React.FC<Props> = ({ where, storyboardId = null, hasKeyframes = false, refreshKey = 0 }) => {
  const [status, setStatus] = useState<TrialStatus | null>(null);
  const { starterPack } = useWallet();

  useEffect(() => {
    let alive = true;
    getTrialStatus().then((s) => { if (alive) setStatus(s); }).catch(() => { /* the Studio works without the line */ });
    return () => { alive = false; };
  }, [refreshKey]);

  // WHICH of the three moments is on screen (or none) — computed once so the funnel events fire per moment, not per render.
  const moment: 'home' | 'frames' | 'bring' | null = !status ? null
    : where === 'home' ? (status.state === 'available' ? 'home' : null)
      : !(status.state === 'available' || status.storyboardId === storyboardId) ? null
        : !hasKeyframes ? (status.state !== 'used' ? 'frames' : null)
          : 'bring';
  const hasStarter = !!starterPack;
  useEffect(() => {
    if (!moment || !status) return;
    if (moment === 'bring') { if (hasStarter) trackStarterPackOfferShown('trial_bring_to_life'); return; }
    trackTrialOfferShown(moment === 'home' ? 'home' : 'episode', status.state);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per moment; status.state rides along with it
  }, [moment, hasStarter]);

  if (!status) return null;
  const line = `flex flex-wrap items-center gap-2 ${TYPE.control} text-secondary-500 dark:text-secondary-400`;

  if (where === 'home') {
    if (status.state !== 'available') return null;
    return (
      <p className={`mt-3 ${line}`} data-testid="trial-notice">
        <Gift className="h-3.5 w-3.5 shrink-0" aria-hidden />
        Your first storyboard is on us: the script and up to {status.framesLeft} shot pictures.
      </p>
    );
  }

  const onThisEpisode = status.state === 'available' || status.storyboardId === storyboardId;
  if (!onThisEpisode) return null;
  if (!hasKeyframes && status.state !== 'used') {
    return (
      <p className={line} data-testid="trial-notice">
        <Gift className="h-3.5 w-3.5 shrink-0" aria-hidden />
        On us: {status.framesLeft} free shot picture{status.framesLeft === 1 ? '' : 's'} left for this storyboard.
      </p>
    );
  }
  if (!hasKeyframes) return null;
  return (
    <div className={`${line} justify-between rounded-lg border border-secondary-200 px-3 py-2 dark:border-secondary-700`} data-testid="trial-bring-to-life">
      <span className="text-secondary-700 dark:text-secondary-300">
        {starterPack
          ? `Bring it to life: ${starterPack.credits.toLocaleString()} credits for $${starterPack.price_usd} animates and publishes this episode.`
          : 'Bring it to life: top up to animate and publish this episode.'}
      </span>
      <Link
        to="/wallet"
        onClick={() => { if (starterPack) trackStarterPackOfferClicked('trial_bring_to_life'); }}
        className={`inline-flex shrink-0 items-center gap-1 rounded-md bg-secondary-900 px-2.5 py-1 ${TYPE.control} font-medium text-white dark:bg-white dark:text-black`}
      >
        {starterPack ? 'Get the starter pack' : 'Top up'} <ArrowRight className="h-3 w-3" aria-hidden />
      </Link>
    </div>
  );
};

export default TrialNotice;
